/**
 * Share links API: generating a token (authenticated, coordinator/admin
 * side) and fetching shared data by token (unauthenticated, bride side).
 *
 * The unauthenticated path calls the `get_shared_layout` Postgres function
 * via RPC — never queries `weddings`/`layouts` tables directly, since
 * those have no public RLS policy at all (see migrations/0003). The RPC
 * is the only door, and it only opens for an exact token match.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabaseClient';
import type { LayoutObject, Room } from '../domain/types';

function generateShareToken(): string {
  // Unguessable, URL-safe token. crypto.randomUUID() is supported in all
  // modern browsers and gives ample entropy for this purpose.
  return crypto.randomUUID().replace(/-/g, '');
}

/**
 * Generates (or returns the existing) share token for a wedding and
 * writes it via a normal authenticated update — covered by the
 * weddings_update_staff RLS policy, no special function needed for this
 * side (PRD §4.1 step 10, §6.1's locked access model).
 */
export function useCreateShareLink() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (weddingId: string): Promise<string> => {
      const token = generateShareToken();
      const { error } = await supabase
        .from('weddings')
        .update({ share_token: token })
        .eq('id', weddingId);

      if (error) throw error;
      return token;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['weddings'] });
    },
  });
}

export interface SharedLayoutData {
  weddingId: string;
  coupleNames: string;
  eventDate: string;
  room: Room;
  guestCount: number;
  objects: LayoutObject[];
}

/**
 * Public, unauthenticated fetch for the bride share view (PRD §4.3,
 * §6.1's "Bride share link" access tier). Calls the get_shared_layout RPC
 * function rather than any table directly.
 */
export function useSharedLayout(token: string | undefined) {
  return useQuery({
    queryKey: ['shared-layout', token],
    queryFn: async (): Promise<SharedLayoutData | null> => {
      const { data, error } = await supabase.rpc('get_shared_layout', { token: token! });

      if (error) throw error;
      if (!data || data.length === 0) return null;

      const row = data[0];
      return {
        weddingId: row.wedding_id,
        coupleNames: row.couple_names,
        eventDate: row.event_date,
        room: row.room,
        guestCount: row.guest_count,
        objects: row.objects as unknown as LayoutObject[],
      };
    },
    enabled: !!token,
    // Bride share view has no login, so retries on a bad/expired token
    // would just hammer the RPC pointlessly — fail fast instead.
    retry: false,
  });
}

export function buildShareUrl(token: string): string {
  return `${window.location.origin}/view/${token}`;
}
